import {
    Datagrid,
    List,
    Show,
    TextField,
    SimpleShowLayout,
    ShowButton,
    useRecordContext,
    useDataProvider,
    downloadCSV,
    useListContext
} from "react-admin";
import React, { useEffect, useState, useMemo } from "react";
import { Stack, Button, Grid, Card, CardContent, Typography, CircularProgress, Box, Checkbox, FormControlLabel, FormGroup, Tooltip, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { Legend, Pie, PieChart, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, CartesianGrid } from "recharts";
import moment from "moment/moment";
import { unparse as convertToCSV } from 'papaparse/papaparse.min';

export function QuestionsList(props) {
    return (
        <List {...props} pagination={false} perPage={1000}>
            <Box sx={{ p: 2 }}>
                <SurveyDashboard />
                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Fragenliste</Typography>
                <Datagrid expand={<QuestionsShow />}>
                    <TextField label="ID" source="_id" />
                    <TextField label="Titel" source="title" />
                    <TextField label="Fragetyp" source="type" />
                    <TextField label="Erforderlich?" source="required" />
                    <ShowButton label="Ergebnisse" />
                </Datagrid>
            </Box>
        </List>
    );
}

const SurveyDashboard = () => {
    const { data: questions, isLoading: questionsLoading } = useListContext();
    const dataProvider = useDataProvider();
    const [allAnswers, setAllAnswers] = useState([]);
    const [loadingAnswers, setLoadingAnswers] = useState(true);
    const [filters, setFilters] = useState({}); // { [questionId]: [valIndex1, valIndex2, ...] }

    useEffect(() => {
        dataProvider.getMany('admin/surveys/answers', {})
            .then(res => {
                setAllAnswers(res.data);
                setLoadingAnswers(false);
            })
            .catch(err => {
                console.error(err);
                setLoadingAnswers(false);
            });
    }, [dataProvider]);

    const handleFilterToggle = (questionId, valueIndex) => {
        const question = questions.find(function (q) { return q._id === questionId; });
        if (!question || question.type !== 'multipleChoice') return;

        setFilters(function (prev) {
            const newFilters = Object.assign({}, prev);
            const currentIndices = newFilters[questionId] || [];

            if (currentIndices.indexOf(valueIndex) > -1) {
                const updated = currentIndices.filter(function (idx) { return idx !== valueIndex; });
                if (updated.length === 0) {
                    delete newFilters[questionId];
                } else {
                    newFilters[questionId] = updated;
                }
            } else {
                newFilters[questionId] = currentIndices.concat([valueIndex]);
            }
            return newFilters;
        });
    };

    const filteredAnswersMap = useMemo(() => {
        const map = {};
        if (!questions) return map;

        questions.forEach(q => {
            const targetQId = q._id;
            const filterKeys = Object.keys(filters);

            if (filterKeys.length === 0) {
                map[targetQId] = allAnswers;
                return;
            }

            map[targetQId] = allAnswers.filter(a => {
                return filterKeys.every(fId => {
                    const questionOfFilter = questions.find(qF => qF._id === fId);
                    if (!questionOfFilter) return true;

                    const isMC = questionOfFilter.type === 'multipleChoice';
                    const isCurrent = fId === targetQId;

                    if (!(isMC && isCurrent)) return true;

                    const selectedIndices = filters[fId];
                    const ans = a.answers[fId];
                    if (ans === undefined) return false;

                    if (questionOfFilter.type === 'multipleChoice') {
                        return Array.isArray(ans) && selectedIndices.every(idx => {
                            return ans.indexOf(idx) > -1;
                        });
                    }

                    if (questionOfFilter.type === 'yesNo') {
                        const val = selectedIndices[0] === 0 ? 'yes' : 'no';
                        return ans === val;
                    }

                    return selectedIndices.indexOf(ans) > -1;
                });
            });
        });
        return map;
    }, [allAnswers, filters, questions]);

    if (questionsLoading || loadingAnswers) return <CircularProgress />;

    return (
        <Box sx={{ flexGrow: 1, mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Umfrage-Übersicht</Typography>
            {Object.keys(filters).length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2">
                        Gefiltert nach: <strong>{Object.keys(filters).length} Fragen</strong>
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => setFilters({})}>
                        Filter zurücksetzen
                    </Button>
                </Box>
            )}
            <Grid container spacing={2}>
                {questions.map(q => (
                    <Grid item xs={12} md={6} lg={4} key={q._id}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle2" sx={{ height: '3em', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {q.title}
                                </Typography>
                                <AnswerVisualization
                                    record={q}
                                    answers={filteredAnswersMap[q._id]}
                                    onFilter={q.type === 'multipleChoice' ? function (valIndex) { return handleFilterToggle(q._id, valIndex); } : undefined}
                                    activeFilterIndices={filters[q._id] || []}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {questions && allAnswers.length > 0 && (
                <CrossAnalysis questions={questions} answers={allAnswers} />
            )}
        </Box>
    );
};

const CrossAnalysis = ({ questions, answers }) => {
    const [questionA, setQuestionA] = useState('');
    const [questionB, setQuestionB] = useState('');

    const chartData = useMemo(() => {
        if (!questionA || !questionB || !answers) return [];

        const qA = questions.find(q => q._id === questionA);
        const qB = questions.find(q => q._id === questionB);
        if (!qA || !qB) return [];

        // Helper to get labels for a question and answer record
        const getLabels = (q, ansRecord) => {
            const val = ansRecord.answers[q._id];
            if (val === undefined || val === null) return ['(Keine Antwort)'];

            if (q.type === 'multipleChoice') {
                if (!Array.isArray(val) || val.length === 0) return ['(Keine Antwort)'];
                return val.map(idx => q.data ? q.data[idx] : idx);
            }
            if (q.type === 'yesNo') {
                return [val === 'yes' ? 'Ja' : 'Nein'];
            }
            return [q.data ? q.data[val] : val];
        };

        // Group by Answer A
        const groups = {};
        // Track all possible Answer B labels for colors/keys
        const allBLabels = new Set();

        answers.forEach(a => {
            const labelsA = getLabels(qA, a);
            const labelsB = getLabels(qB, a);

            labelsA.forEach(lA => {
                if (!groups[lA]) groups[lA] = { name: lA, total: 0, counts: {} };
                groups[lA].total += 1; // Count respondent once per A-label

                labelsB.forEach(lB => {
                    groups[lA].counts[lB] = (groups[lA].counts[lB] || 0) + 1;
                    allBLabels.add(lB);
                });
            });
        });

        // Current limitation: If Q_B is multiple choice, sums might > total. 
        // For stacked 100% chart, we normalize based on the sum of segments, not necessarily respondent count.
        // However, "50% of 18-24 are Unemployed" implies Denom = Count(18-24).
        // If someone is Unemployed AND Student (if Q_B allows), they add to both stacks. 
        // Sum of percentages > 100% is confusing in a single stacked bar.
        // Standard approach: Normalize so bar height = 100%.

        const data = Object.values(groups).map(g => {
            const entry = { name: g.name };
            let sumCounts = 0;
            Object.values(g.counts).forEach(c => sumCounts += c);

            // Avoid division by zero
            const denom = sumCounts > 0 ? sumCounts : 1;

            Array.from(allBLabels).forEach(lB => {
                const count = g.counts[lB] || 0;
                // Calculate percentage relative to sum of recorded B-answers (distribution of answers)
                entry[lB] = parseFloat((count / denom * 100).toFixed(1));
            });
            return entry;
        });

        // Sort data by original question A order
        let orderedLabelsA = [];
        if (qA.type === 'yesNo') {
            orderedLabelsA = ['Ja', 'Nein'];
        } else if (qA.data) {
            orderedLabelsA = Object.values(qA.data);
        }

        data.sort((a, b) => {
            const idxA = orderedLabelsA.indexOf(a.name);
            const idxB = orderedLabelsA.indexOf(b.name);
            // If both are found, sort by index
            if (idxA > -1 && idxB > -1) return idxA - idxB;
            // If only A is found, it comes first
            if (idxA > -1) return -1;
            // If only B is found, it comes first
            if (idxB > -1) return 1;
            // Otherwise maintain existing sorted order (or alphabetical)
            return a.name.localeCompare(b.name);
        });

        // Sort keys by original question B order
        let orderedLabelsB = [];
        if (qB.type === 'yesNo') {
            orderedLabelsB = ['Ja', 'Nein'];
        } else if (qB.data) {
            orderedLabelsB = Object.values(qB.data);
        }

        const sortedKeys = Array.from(allBLabels).sort((a, b) => {
            const idxA = orderedLabelsB.indexOf(a);
            const idxB = orderedLabelsB.indexOf(b);
            if (idxA > -1 && idxB > -1) return idxA - idxB;
            if (idxA > -1) return -1;
            if (idxB > -1) return 1;
            return a.localeCompare(b);
        });

        return { data, keys: sortedKeys };
    }, [questionA, questionB, answers, questions]);

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#a4de6c', '#d0ed57'];

    return (
        <Card sx={{ mt: 4, enable: 'flex' }}>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Kreuzanalyse</Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Basis-Frage (Gruppe)</InputLabel>
                        <Select value={questionA} label="Basis-Frage (Gruppe)" onChange={e => setQuestionA(e.target.value)}>
                            {questions.map(q => <MenuItem key={q._id} value={q._id}>{q.title}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Aufschlüsselung nach</InputLabel>
                        <Select value={questionB} label="Aufschlüsselung nach" onChange={e => setQuestionB(e.target.value)}>
                            {questions.map(q => <MenuItem key={q._id} value={q._id}>{q.title}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Stack>
                {chartData.data && chartData.data.length > 0 ? (
                    <Box sx={{ width: '100%', height: 400 }}>
                        <ResponsiveContainer>
                            <BarChart layout="vertical" data={chartData.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" unit="%" domain={[0, 100]} />
                                <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                                <RechartsTooltip />
                                <Legend />
                                {chartData.keys.map((key, index) => (
                                    <Bar key={key} dataKey={key} stackId="a" fill={COLORS[index % COLORS.length]} />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Bitte wählen Sie zwei Fragen aus, um die Analyse zu starten.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );


};

export function QuestionsShow(props) {
    return <Show {...props}>
        <SimpleShowLayout>
            <TextField label="ID" source="_id" />
            <TextField label="Titel" source="title" />
            <TextField label="Fragetyp" source="type" />
            <TextField label="Erforderlich?" source="required" />
            <AnswerVisualization />
        </SimpleShowLayout>
    </Show>
}

const AnswerVisualization = ({ record: recordProp, answers: answersProp, onFilter, activeFilterIndices }) => {
    const recordContext = useRecordContext();
    const record = recordProp || recordContext;
    const dataProvider = useDataProvider();
    const [answers, setAnswers] = useState(null);
    const activeFilterIndicesSafe = activeFilterIndices || [];

    useEffect(() => {
        if (answersProp) {
            setAnswers(answersProp);
        } else if (record) {
            dataProvider.getMany(`admin/surveys/answers`, { 'for': record._id })
                .then(res => {
                    setAnswers(res.data)
                })
                .catch(error => console.error(error))
        }
    }, [answersProp, record, dataProvider]);

    const { counts, answersLabeled, original, mainCounts, dependentCounts } = useMemo(() => {
        if (!record || !answers) return { counts: [], answersLabeled: [], original: [], mainCounts: [], dependentCounts: [] };

        const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
        for (let alpha = 80; alpha >= 40; alpha -= 20) {
            COLORS.push(...COLORS.map(color => color + alpha))
        }

        let answersLabeledRaw = [];
        let answersDf = [];
        const currentAnswers = (Array.isArray(answers) ? answers : (answers.data || []))
            .filter(a => a.answers && a.answers[record._id] !== undefined);

        switch (record.type) {
            case 'yesNo':
                answersLabeledRaw = currentAnswers.map(a => ({
                    _id: a._id,
                    answerText: a.answers[record._id] === 'yes' ? 'Ja' : 'Nein', ...a
                }))
                answersDf = answersLabeledRaw.map(a => a.answerText)
                break;
            case 'multipleChoice':
                answersLabeledRaw = currentAnswers.map(a => {
                    const selectedIndexes = a.answers[record._id] || [];
                    const selectedTexts = selectedIndexes
                        .map(idx => record.data ? record.data[idx] : undefined)
                        .filter(t => t !== undefined);
                    return {
                        _id: a._id,
                        answerText: selectedTexts.join(', '),
                        selectedIndexes,
                        ...a
                    }
                })
                answersDf = answersLabeledRaw.reduce((acc, a) => {
                    const mapped = (a.selectedIndexes || [])
                        .map(idx => record.data ? record.data[idx] : undefined)
                        .filter(t => t !== undefined);
                    return acc.concat(mapped);
                }, []);
                break;
            default:
                answersLabeledRaw = currentAnswers.map(a => ({
                    _id: a._id,
                    answerText: record.data ? record.data[a.answers[record._id]] : a.answers[record._id], ...a
                }))
                answersDf = answersLabeledRaw.map(a => a.answerText)
                break;
        }

        const countsMap = answersDf.reduce((acc, curr) => {
            if (curr !== undefined) acc[curr] = (acc[curr] || 0) + 1;
            return acc;
        }, {});

        const original = record.type === 'yesNo' ? ['Ja', 'Nein'] : (record.data ? Object.values(record.data) : []);

        const counts = original.map((answer, index) => ({
            answer,
            count: countsMap[answer] || 0,
            fill: COLORS[index % COLORS.length]
        }));

        const mainCounts = [];
        const dependentCounts = [];

        // Identify dependent answers if available
        const dependentIndices = record.dependentAnswers || [];

        // Calculate percentages relative to total respondents for multipleChoice
        if (record.type === 'multipleChoice') {
            const totalRespondents = currentAnswers.length;
            const entries = record.data ? Object.entries(record.data) : [];

            entries.forEach(([key, label], index) => {
                const count = countsMap[label] || 0;
                const percentage = totalRespondents > 0 ? parseFloat((count / totalRespondents * 100).toFixed(1)) : 0;
                const item = {
                    answer: label,
                    count,
                    percentage,
                    fill: COLORS[index % COLORS.length]
                };

                // loose comparison for keys which might be string "0" vs number 0
                if (dependentIndices.some(d => String(d) === String(key))) {
                    dependentCounts.push(item);
                } else {
                    mainCounts.push(item);
                }
            });
        }

        return {
            counts,
            answersLabeled: answersLabeledRaw.map(a => flattenObject(a)),
            original,
            mainCounts,
            dependentCounts
        };
    }, [answers, record]);

    if (!record || !answers) return <CircularProgress />;

    const totalAnswersCount = (Array.isArray(answers) ? answers : (answers.data || [])).length;

    const handleExport = () => {
        if (answersLabeled.length === 0) return;
        let currentDate = moment().format("YYMMDDTHHmmss");
        const csv = convertToCSV({
            data: answersLabeled,
            fields: Object.keys(answersLabeled[0] || {}).filter(a => !['__v', 'id'].includes(a))
                .filter(a => !a.startsWith('answers.'))
        });
        downloadCSV(csv, `${record._id}-export-answers-${currentDate}`);
    };

    const handlePieClick = (data) => {
        if (onFilter) {
            const valueIndex = original.indexOf(data.answer);
            onFilter(valueIndex);
        }
    };

    const handleBarClick = (data) => {
        if (onFilter && data && data.activePayload && data.activePayload.length > 0) {
            const payload = data.activePayload[0].payload;
            const valueIndex = original.indexOf(payload.answer);
            if (valueIndex > -1) onFilter(valueIndex);
        }
    };

    return <Stack alignItems="center" sx={{ width: '100%' }}>
        <Box sx={{ width: '100%', height: 250 }}>
            {record.type === 'multipleChoice' ? (
                <Stack direction="row" spacing={2} sx={{ height: '100%' }}>
                    <Box sx={{ flex: 1, height: '100%', minWidth: 0 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                layout="vertical"
                                data={mainCounts.filter(entry => activeFilterIndicesSafe.indexOf(original.indexOf(entry.answer)) === -1)}
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                onClick={handleBarClick}
                                style={{ cursor: onFilter ? 'pointer' : 'default' }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" domain={[0, 100]} unit="%" />
                                <YAxis dataKey="answer" type="category" width={100} tick={{ fontSize: 10 }} />
                                <RechartsTooltip
                                    formatter={(value, name, props) => [`${value}% (${props.payload.count})`, 'Anteil']}
                                />
                                <Bar dataKey="percentage" name="Prozent" radius={[0, 4, 4, 0]}>
                                    {mainCounts.filter(entry => activeFilterIndicesSafe.indexOf(original.indexOf(entry.answer)) === -1).map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={entry.fill}
                                            stroke={'none'}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    {dependentCounts.length > 0 && (
                        <Box sx={{ width: '40%', height: '100%', borderLeft: '1px solid #eee', pl: 1, minWidth: 0 }}>
                            <Typography variant="caption" align="center" display="block" sx={{ mb: 1 }}>
                                Abhängige Antworten
                            </Typography>
                            <ResponsiveContainer width="100%" height="90%">
                                <BarChart
                                    layout="vertical"
                                    data={dependentCounts.filter(entry => activeFilterIndicesSafe.indexOf(original.indexOf(entry.answer)) === -1)}
                                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                    onClick={handleBarClick}
                                    style={{ cursor: onFilter ? 'pointer' : 'default' }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" domain={[0, 100]} unit="%" />
                                    <YAxis dataKey="answer" type="category" width={100} tick={{ fontSize: 10 }} />
                                    <RechartsTooltip
                                        formatter={(value, name, props) => [`${value}% (${props.payload.count})`, 'Anteil']}
                                    />
                                    <Bar dataKey="percentage" name="Prozent" radius={[0, 4, 4, 0]}>
                                        {dependentCounts.filter(entry => activeFilterIndicesSafe.indexOf(original.indexOf(entry.answer)) === -1).map((entry, index) => (
                                            <Cell
                                                key={`cell-mv-${index}`}
                                                fill={entry.fill}
                                                stroke={'none'}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    )}
                </Stack>
            ) : (
                <ResponsiveContainer>
                    <PieChart>
                        <Pie
                            data={counts.filter(c => c.count > 0)}
                            dataKey='count'
                            nameKey='answer'
                            label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                            animationDuration={500}
                            onClick={handlePieClick}
                            style={{ cursor: onFilter ? 'pointer' : 'default' }}
                        >
                            {counts.filter(c => c.count > 0).map((entry, index) => {
                                const isSelected = activeFilterIndicesSafe.indexOf(original.indexOf(entry.answer)) > -1;
                                return (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.fill}
                                        stroke={isSelected ? '#000' : '#fff'}
                                        strokeWidth={isSelected ? 3 : 1}
                                    />
                                );
                            })}
                        </Pie>
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            )}
        </Box>
        {record.type === 'multipleChoice' && onFilter && (
            <Box sx={{ width: '100%', mt: 2, maxHeight: 200, overflowY: 'auto' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                    Deaktivierte Optionen haben in der aktuellen Filterkombination keine Ergebnisse.
                </Typography>
                <FormGroup>
                    {counts.map((item) => {
                        const valIndex = original.indexOf(item.answer);
                        const isChecked = activeFilterIndicesSafe.indexOf(valIndex) > -1;
                        const percentage = totalAnswersCount > 0 ? (item.count / totalAnswersCount * 100).toFixed(0) : 0;
                        const isDisabled = !isChecked && item.count === 0;

                        const control = (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isChecked}
                                        onChange={() => onFilter(valIndex)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {item.answer} ({item.count}, {percentage}%)
                                    </Typography>
                                }
                                disabled={isDisabled}
                            />
                        );

                        if (isDisabled) {
                            return (
                                <Tooltip key={valIndex} title="Keine Ergebnisse für diese Kombination" placement="top-start">
                                    <span style={{ display: 'inline-block', width: '100%' }}>
                                        {control}
                                    </span>
                                </Tooltip>
                            );
                        }

                        return <React.Fragment key={valIndex}>{control}</React.Fragment>;
                    })}
                </FormGroup>
            </Box>
        )}
        <Button size="small" onClick={handleExport} sx={{ mt: 1 }}>Antworten exportieren (CSV)</Button>
    </Stack>
}

function flattenObject(obj, parentKey = '', result = {}) {
    for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
            let propName = parentKey ? `${parentKey}.${key}` : key;
            if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                flattenObject(obj[key], propName, result);
            } else {
                result[propName] = obj[key];
            }
        }
    }
    return result;
}